import { ProductExcludeBranchRuleSchema } from '@diamond/shared/schema/product';
import { Value } from '@sinclair/typebox/value';
import type { CellValue } from 'exceljs';

import { ExclusionRule } from '../api';

export const IMPORT_HEADERS = [
  'product_item_code',
  'distribution_channel',
  'plant',
  'cg_1_business_segmentation',
];
export const MAX_IMPORT_BYTES = 5 * 1024 * 1024;
export interface ImportRow {
  row: number;
  values: string[];
  errors: string[];
}
export interface ImportPreview {
  rows: ImportRow[];
  items: ExclusionRule[];
  duplicateCount: number;
}

export function validateImportFile(file: File): void {
  if (!file.name.toLowerCase().endsWith('.xlsx'))
    throw new Error('Choose an .xlsx file.');
  if (file.size > MAX_IMPORT_BYTES)
    throw new Error('The file must be 5 MiB or smaller.');
}

function cellText(value: CellValue): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number')
    return String(value).trim();
  throw new Error(
    'Use plain text or numbers; formulas and formatted objects are not supported.'
  );
}

export function parseImportRows(
  headers: string[],
  input: { row: number; cells: CellValue[] }[]
): ImportPreview {
  const normalizedHeaders = headers.map((header) => header.trim());
  if (
    normalizedHeaders.length < 3 ||
    normalizedHeaders.length > 4 ||
    normalizedHeaders.some((header, index) => header !== IMPORT_HEADERS[index])
  ) {
    throw new Error(
      `Expected columns: ${IMPORT_HEADERS.join(', ')}. CG1 is optional.`
    );
  }
  const populated = input.filter(({ cells }) =>
    cells.some((cell) => cell !== null && cell !== undefined && cell !== '')
  );
  if (!populated.length)
    throw new Error('The worksheet has no rules to import.');
  if (populated.length > 1000)
    throw new Error('Import at most 1,000 populated rows in one file.');
  const rows: ImportRow[] = [];
  const items: ExclusionRule[] = [];
  const keys = new Set<string>();
  let duplicateCount = 0;
  for (const inputRow of populated) {
    const errors: string[] = [];
    const values = inputRow.cells.map((cell, index) => {
      try {
        return cellText(cell);
      } catch {
        errors.push(
          `${
            IMPORT_HEADERS[index] ?? 'Extra column'
          }: use plain text or numbers, not formulas.`
        );
        return '';
      }
    });
    while (values.length < 4) values.push('');
    if (values.slice(normalizedHeaders.length).some(Boolean))
      errors.push('Remove values from extra columns.');
    const [product, channel, plant, cg1] = values;
    if (
      !/^\d+$/.test(product) ||
      !Number.isSafeInteger(Number(product)) ||
      Number(product) < 1
    )
      errors.push('product_item_code: enter a positive integer.');
    if (!channel) errors.push('distribution_channel: required.');
    if (
      !/^\d+$/.test(plant) ||
      !Number.isSafeInteger(Number(plant)) ||
      Number(plant) < 1
    )
      errors.push('plant: enter a positive integer.');
    const rule = {
      product_item_code: Number(product),
      distribution_channel: channel,
      plant: Number(plant),
      cg_1_business_segmentation: cg1 || null,
    };
    if (
      errors.length === 0 &&
      Value.Check(ProductExcludeBranchRuleSchema, rule)
    ) {
      const key = JSON.stringify([
        rule.product_item_code,
        channel,
        rule.plant,
        rule.cg_1_business_segmentation,
      ]);
      if (keys.has(key)) duplicateCount += 1;
      else {
        keys.add(key);
        items.push(rule);
      }
    }
    rows.push({ row: inputRow.row, values: values.slice(0, 4), errors });
  }
  return { rows, items, duplicateCount };
}

export async function readImportFile(file: File): Promise<ImportPreview> {
  validateImportFile(file);
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const sheet = workbook.worksheets[0];
  if (!sheet) throw new Error('The workbook has no worksheet.');
  const header = sheet.getRow(1);
  const headers = Array.from({ length: header.cellCount }, (_, index) =>
    cellText(header.getCell(index + 1).value)
  );
  const rows: { row: number; cells: CellValue[] }[] = [];
  sheet.eachRow((row, index) => {
    if (index !== 1)
      rows.push({
        row: index,
        cells: Array.from(
          { length: Math.max(4, row.cellCount) },
          (_, column) => row.getCell(column + 1).value
        ),
      });
  });
  return parseImportRows(headers, rows);
}

export async function downloadImportTemplate(): Promise<void> {
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();
  const sheet = workbook.addWorksheet('Exclusions');
  sheet.addRow(IMPORT_HEADERS);
  sheet.columns.forEach((column) => {
    column.width = 30;
    column.numFmt = '@';
  });
  const buffer = await workbook.xlsx.writeBuffer();
  const url = URL.createObjectURL(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = 'exclude-branch-template.xlsx';
  link.click();
  URL.revokeObjectURL(url);
}
